import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Paper, IconButton, Button, Chip, TextField, MenuItem, Select,
  FormControl, InputLabel, Dialog, DialogTitle, DialogContent, DialogActions,
  Switch, FormControlLabel, CircularProgress, Snackbar, Alert, Tooltip,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import CameraEnhanceRoundedIcon from '@mui/icons-material/CameraEnhanceRounded';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';

const API_URL = '/api';

function getHeaders() {
  const userId = localStorage.getItem('user_id');
  return userId
    ? { 'X-User-Id': String(userId), 'Content-Type': 'application/json' }
    : { 'Content-Type': 'application/json' };
}

const CATEGORIAS = [
  { value: 'medicina',    label: 'Medicina general',  color: '#0ea5e9' },
  { value: 'estetica',    label: 'Estética',          color: '#ec4899' },
  { value: 'odontologia', label: 'Odontología',       color: '#06b6d4' },
  { value: 'pediatria',   label: 'Pediatría',         color: '#f59e0b' },
  { value: 'veterinaria', label: 'Veterinaria',       color: '#10b981' },
  { value: 'otra',        label: 'Otra',              color: '#64748b' },
];

const ICONOS = [
  'AutoAwesomeRounded',
  'HealingRounded',
  'MedicalServicesRounded',
  'FavoriteRounded',
  'ChildCareRounded',
  'RemoveRedEyeRounded',
  'AccessibilityNewRounded',
  'PetsRounded',
  'LocalPharmacyRounded',
];

export default function AdminPlantillasChatbot() {
  const [plantillas, setPlantillas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snack, setSnack] = useState(null);
  const [editDialog, setEditDialog] = useState(false);
  const [snapshotDialog, setSnapshotDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  // Snapshot form
  const [chatbots, setChatbots] = useState([]);
  const [snapForm, setSnapForm] = useState({
    chatbot_id: '', nombre: '', descripcion: '', especialidad: '',
    categoria: 'medicina', icono: 'AutoAwesomeRounded', color: '#6366f1',
    excluir_categorias_kb: 'ubicacion, personal',
  });

  const fetchPlantillas = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/admin/smart-flow-templates`, { headers: getHeaders() });
      if (!r.ok) throw new Error();
      setPlantillas(await r.json());
    } catch (e) {
      setSnack({ severity: 'error', msg: 'Error cargando plantillas' });
    } finally {
      setLoading(false);
    }
  };

  const fetchChatbotsList = async () => {
    try {
      const r = await fetch(`${API_URL}/smart-flows/chatbots`, { headers: getHeaders() });
      if (r.ok) setChatbots(await r.json());
    } catch (_) {}
  };

  useEffect(() => {
    fetchPlantillas();
    fetchChatbotsList();
  }, []);

  const handleAbrirEditar = (p) => {
    setEditing(p);
    setForm({
      nombre: p.nombre,
      descripcion: p.descripcion || '',
      especialidad: p.especialidad || '',
      categoria: p.categoria || 'medicina',
      icono: p.icono || 'AutoAwesomeRounded',
      color: p.color || '#6366f1',
      is_published: p.is_premium ? false : true,
      is_premium: !!p.is_premium,
    });
    // Para el switch is_published, necesitamos consultar el detalle:
    fetch(`${API_URL}/smart-flow-templates/${p.id}`, { headers: getHeaders() })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => {
        if (d) setForm((prev) => ({ ...prev, is_published: !!d.is_published }));
      });
    setEditDialog(true);
  };

  const handleGuardar = async () => {
    if (!editing) return;
    try {
      const r = await fetch(`${API_URL}/admin/smart-flow-templates/${editing.id}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(form),
      });
      if (!r.ok) throw new Error();
      setSnack({ severity: 'success', msg: 'Plantilla actualizada' });
      setEditDialog(false);
      fetchPlantillas();
    } catch (e) {
      setSnack({ severity: 'error', msg: 'Error guardando' });
    }
  };

  const handleEliminar = async (p) => {
    if (!window.confirm(`¿Eliminar la plantilla "${p.nombre}"?`)) return;
    try {
      const r = await fetch(`${API_URL}/admin/smart-flow-templates/${p.id}`, {
        method: 'DELETE',
        headers: getHeaders(),
      });
      if (!r.ok) throw new Error();
      setSnack({ severity: 'success', msg: 'Plantilla eliminada' });
      fetchPlantillas();
    } catch (e) {
      setSnack({ severity: 'error', msg: 'Error eliminando' });
    }
  };

  const handleTogglePublicado = async (p, nuevoEstado) => {
    try {
      const r = await fetch(`${API_URL}/admin/smart-flow-templates/${p.id}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({ is_published: nuevoEstado }),
      });
      if (!r.ok) throw new Error();
      fetchPlantillas();
    } catch (e) {
      setSnack({ severity: 'error', msg: 'Error actualizando' });
    }
  };

  const handleSnapshot = async () => {
    if (!snapForm.chatbot_id || !snapForm.nombre) {
      setSnack({ severity: 'warning', msg: 'Chatbot y nombre son obligatorios' });
      return;
    }
    try {
      const payload = {
        chatbot_id: parseInt(snapForm.chatbot_id, 10),
        nombre: snapForm.nombre,
        descripcion: snapForm.descripcion,
        especialidad: snapForm.especialidad,
        categoria: snapForm.categoria,
        icono: snapForm.icono,
        color: snapForm.color,
        excluir_categorias_kb: snapForm.excluir_categorias_kb
          .split(',').map((s) => s.trim()).filter(Boolean),
      };
      const r = await fetch(`${API_URL}/admin/smart-flow-templates/from-chatbot`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error(await r.text());
      setSnack({ severity: 'success', msg: 'Plantilla creada desde el chatbot' });
      setSnapshotDialog(false);
      setSnapForm({
        chatbot_id: '', nombre: '', descripcion: '', especialidad: '',
        categoria: 'medicina', icono: 'AutoAwesomeRounded', color: '#6366f1',
        excluir_categorias_kb: 'ubicacion, personal',
      });
      fetchPlantillas();
    } catch (e) {
      setSnack({ severity: 'error', msg: 'Error creando plantilla' });
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundImage: `
        radial-gradient(circle at 20% 50%, rgba(99, 102, 241, 0.08) 0%, transparent 50%),
        radial-gradient(circle at 80% 20%, rgba(168, 85, 247, 0.08) 0%, transparent 40%)
      `,
      pb: 6,
    }}>
      <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1300, mx: 'auto' }}>
        {/* Header */}
        <Box sx={{
          mb: 3, p: { xs: 2.5, md: 3 },
          borderRadius: 4,
          background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)',
          color: '#fff',
          boxShadow: '0 8px 24px rgba(30, 58, 95, 0.20)',
          display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', rowGap: 2,
        }}>
          <Box sx={{
            width: 48, height: 48, borderRadius: 3,
            background: 'linear-gradient(135deg, #a855f7, #6366f1)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(168,85,247,0.4)',
          }}>
            <MenuBookRoundedIcon sx={{ fontSize: 26 }}/>
          </Box>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography sx={{ fontWeight: 800, fontSize: { xs: 20, md: 24 }, letterSpacing: -0.3 }}>
              Plantillas de chatbot
            </Typography>
            <Typography sx={{ opacity: 0.78, fontSize: 13, fontWeight: 500 }}>
              Galería que los doctores ven al crear/configurar su Smart Flow
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined" startIcon={<CameraEnhanceRoundedIcon />}
              onClick={() => setSnapshotDialog(true)}
              sx={{
                color: '#fff', borderColor: 'rgba(255,255,255,0.4)',
                textTransform: 'none', fontWeight: 700, borderRadius: 2.5,
                '&:hover': { borderColor: '#fff', background: 'rgba(255,255,255,0.08)' },
              }}
            >
              Desde chatbot
            </Button>
          </Box>
        </Box>

        {/* Lista */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : plantillas.length === 0 ? (
          <Paper elevation={0} sx={{
            p: 6, borderRadius: 4, textAlign: 'center',
            background: '#fff', border: '1px solid #e2e8f0',
          }}>
            <MenuBookRoundedIcon sx={{ fontSize: 64, color: '#cbd5e1', mb: 1.5 }}/>
            <Typography sx={{ fontWeight: 800, fontSize: 17, color: '#0f172a', mb: 0.5 }}>
              Aún no hay plantillas
            </Typography>
            <Typography sx={{ fontSize: 13, color: '#64748b', mb: 2.5 }}>
              Crea la primera desde un chatbot existente (NARA Tech)
            </Typography>
            <Button
              variant="contained" startIcon={<CameraEnhanceRoundedIcon />}
              onClick={() => setSnapshotDialog(true)}
              sx={{
                textTransform: 'none', fontWeight: 700, borderRadius: 2.5,
                background: 'linear-gradient(135deg, #a855f7, #6366f1)',
                boxShadow: '0 4px 12px rgba(168,85,247,0.35)',
                px: 3,
              }}
            >
              Crear desde chatbot
            </Button>
          </Paper>
        ) : (
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
            gap: 2,
          }}>
            {plantillas.map((p) => (
              <Paper key={p.id} elevation={0} sx={{
                p: 2.5, borderRadius: 3, background: '#fff',
                border: '1px solid #e2e8f0',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute', top: 0, left: 0, right: 0,
                  height: 3, background: p.color || '#6366f1',
                },
              }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1 }}>
                  <Box sx={{
                    width: 38, height: 38, borderRadius: 2.5,
                    background: `linear-gradient(135deg, ${p.color || '#6366f1'}, ${p.color || '#6366f1'}cc)`,
                    color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <MenuBookRoundedIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography sx={{
                        fontWeight: 800, fontSize: 14.5, color: '#0f172a',
                        flex: 1, minWidth: 0,
                        overflow: 'hidden', textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>
                        {p.nombre}
                      </Typography>
                      {p.is_premium && <StarRoundedIcon sx={{ fontSize: 16, color: '#f59e0b' }}/>}
                    </Box>
                    {p.especialidad && (
                      <Chip
                        size="small" label={p.especialidad}
                        sx={{
                          height: 18, fontSize: 10, fontWeight: 700, mt: 0.4,
                          backgroundColor: (p.color || '#6366f1') + '18',
                          color: p.color || '#6366f1',
                          border: 'none',
                        }}
                      />
                    )}
                  </Box>
                </Box>
                <Typography sx={{
                  fontSize: 12, color: '#64748b', lineHeight: 1.45, mb: 1.5,
                  overflow: 'hidden', textOverflow: 'ellipsis',
                  display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                  minHeight: 35,
                }}>
                  {p.descripcion || '—'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.25, mb: 1.5, flexWrap: 'wrap' }}>
                  <Stat icon={<BoltRoundedIcon sx={{ fontSize: 12 }}/>} color="#6366f1" label={`${p.intents_count} intents`} />
                  <Stat icon={<MenuBookRoundedIcon sx={{ fontSize: 12 }}/>} color="#0ea5e9" label={`${p.kb_count} KB`} />
                  {p.veces_usada > 0 && (
                    <Stat icon={<CheckCircleRoundedIcon sx={{ fontSize: 12 }}/>} color="#10b981" label={`${p.veces_usada} usos`} />
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'space-between', pt: 1, borderTop: '1px solid #f1f5f9' }}>
                  <Tooltip title="Editar">
                    <IconButton size="small" onClick={() => handleAbrirEditar(p)} sx={{ color: '#6366f1' }}>
                      <EditRoundedIcon sx={{ fontSize: 18 }}/>
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Eliminar">
                    <IconButton size="small" onClick={() => handleEliminar(p)} sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}>
                      <DeleteOutlineRoundedIcon sx={{ fontSize: 18 }}/>
                    </IconButton>
                  </Tooltip>
                  <Box sx={{ flex: 1 }} />
                </Box>
              </Paper>
            ))}
          </Box>
        )}
      </Box>

      {/* Dialog editar */}
      <Dialog
        open={editDialog} onClose={() => setEditDialog(false)}
        fullWidth maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}
      >
        <Box sx={{ background: 'linear-gradient(135deg, #1e3a5f, #2d5a87)', color: '#fff', p: 2.5 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 17 }}>Editar plantilla</Typography>
          <Typography sx={{ opacity: 0.78, fontSize: 12 }}>{editing?.nombre}</Typography>
        </Box>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField label="Nombre" fullWidth size="small" value={form.nombre || ''}
              onChange={(e) => setForm({ ...form, nombre: e.target.value })} />
            <TextField label="Descripción" fullWidth multiline rows={3} size="small" value={form.descripcion || ''}
              onChange={(e) => setForm({ ...form, descripcion: e.target.value })} />
            <TextField label="Especialidad" fullWidth size="small" value={form.especialidad || ''}
              onChange={(e) => setForm({ ...form, especialidad: e.target.value })}
              placeholder="Ej: Cirugía Estética" />
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ flex: 1, minWidth: 150 }}>
                <InputLabel>Categoría</InputLabel>
                <Select label="Categoría" value={form.categoria || 'medicina'}
                  onChange={(e) => setForm({ ...form, categoria: e.target.value })}>
                  {CATEGORIAS.map((c) => (
                    <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ flex: 1, minWidth: 150 }}>
                <InputLabel>Icono</InputLabel>
                <Select label="Icono" value={form.icono || 'AutoAwesomeRounded'}
                  onChange={(e) => setForm({ ...form, icono: e.target.value })}>
                  {ICONOS.map((i) => (
                    <MenuItem key={i} value={i}>{i.replace('Rounded', '')}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <TextField label="Color (hex)" size="small" value={form.color || '#6366f1'}
              onChange={(e) => setForm({ ...form, color: e.target.value })}
              placeholder="#6366f1"
              InputProps={{
                startAdornment: (
                  <Box sx={{ width: 22, height: 22, borderRadius: 1, background: form.color, mr: 1, border: '1px solid #e2e8f0' }}/>
                ),
              }}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControlLabel
                control={<Switch checked={!!form.is_published}
                  onChange={(e) => setForm({ ...form, is_published: e.target.checked })} />}
                label="Publicada"
              />
              <FormControlLabel
                control={<Switch checked={!!form.is_premium}
                  onChange={(e) => setForm({ ...form, is_premium: e.target.checked })} />}
                label="Premium"
              />
            </Box>
            <Typography sx={{ fontSize: 11.5, color: '#64748b', fontStyle: 'italic' }}>
              El contenido (system_prompt, intents, KB) no es editable desde aquí. Para
              actualizarlo, vuelve a tomar un snapshot del chatbot original o edita la
              plantilla directamente en BD.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setEditDialog(false)} sx={{ textTransform: 'none', color: '#64748b' }}>Cancelar</Button>
          <Button variant="contained" onClick={handleGuardar}
            sx={{
              textTransform: 'none', fontWeight: 700,
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              boxShadow: '0 4px 12px rgba(99, 102, 241, 0.35)',
              px: 3,
            }}
          >
            Guardar cambios
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog snapshot desde chatbot */}
      <Dialog
        open={snapshotDialog} onClose={() => setSnapshotDialog(false)}
        fullWidth maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}
      >
        <Box sx={{ background: 'linear-gradient(135deg, #a855f7, #6366f1)', color: '#fff', p: 2.5 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 17 }}>Crear plantilla desde chatbot</Typography>
          <Typography sx={{ opacity: 0.85, fontSize: 12 }}>
            Toma una "foto" del system prompt, intents y KB de un chatbot existente
          </Typography>
        </Box>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>Chatbot origen</InputLabel>
              <Select label="Chatbot origen" value={snapForm.chatbot_id}
                onChange={(e) => setSnapForm({ ...snapForm, chatbot_id: e.target.value })}>
                {chatbots.map((cb) => (
                  <MenuItem key={cb.id} value={String(cb.id)}>{cb.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField label="Nombre de la plantilla" fullWidth size="small" required
              value={snapForm.nombre}
              onChange={(e) => setSnapForm({ ...snapForm, nombre: e.target.value })}
              placeholder="Ej: Cirugía Estética — Consulta y agendamiento" />
            <TextField label="Descripción" multiline rows={3} fullWidth size="small"
              value={snapForm.descripcion}
              onChange={(e) => setSnapForm({ ...snapForm, descripcion: e.target.value })} />
            <TextField label="Especialidad" fullWidth size="small"
              value={snapForm.especialidad}
              onChange={(e) => setSnapForm({ ...snapForm, especialidad: e.target.value })}
              placeholder="Ej: Cirugía Estética" />
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ flex: 1, minWidth: 150 }}>
                <InputLabel>Categoría</InputLabel>
                <Select label="Categoría" value={snapForm.categoria}
                  onChange={(e) => setSnapForm({ ...snapForm, categoria: e.target.value })}>
                  {CATEGORIAS.map((c) => (
                    <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ flex: 1, minWidth: 150 }}>
                <InputLabel>Icono</InputLabel>
                <Select label="Icono" value={snapForm.icono}
                  onChange={(e) => setSnapForm({ ...snapForm, icono: e.target.value })}>
                  {ICONOS.map((i) => (
                    <MenuItem key={i} value={i}>{i.replace('Rounded', '')}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <TextField label="Color (hex)" size="small" value={snapForm.color}
              onChange={(e) => setSnapForm({ ...snapForm, color: e.target.value })} />
            <Box sx={{ p: 1.5, borderRadius: 2, background: '#fef3c7', border: '1px solid #fcd34d' }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#92400e', mb: 0.5 }}>
                Sanitización
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: '#92400e', mb: 1, lineHeight: 1.45 }}>
                Excluir KB de estas categorías (datos personales del autor original).
                Por defecto se filtra "ubicación" y "personal".
              </Typography>
              <TextField size="small" fullWidth
                value={snapForm.excluir_categorias_kb}
                onChange={(e) => setSnapForm({ ...snapForm, excluir_categorias_kb: e.target.value })}
                placeholder="ubicacion, personal"
                sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#fff' } }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setSnapshotDialog(false)} sx={{ textTransform: 'none', color: '#64748b' }}>Cancelar</Button>
          <Button variant="contained" onClick={handleSnapshot}
            sx={{
              textTransform: 'none', fontWeight: 700,
              background: 'linear-gradient(135deg, #a855f7, #6366f1)',
              boxShadow: '0 4px 12px rgba(168,85,247,0.35)',
              px: 3,
            }}
          >
            Crear plantilla
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snack} autoHideDuration={2500}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snack && (
          <Alert severity={snack.severity} variant="filled" sx={{ borderRadius: 2 }}>
            {snack.msg}
          </Alert>
        )}
      </Snackbar>
    </Box>
  );
}

function Stat({ icon, color, label }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Box sx={{ color, display: 'flex' }}>{icon}</Box>
      <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>
        {label}
      </Typography>
    </Box>
  );
}
